import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Sparkles, User as UserIcon } from 'lucide-react';
import { Alert, Badge, Button, Disclaimer, Textarea } from '../components/ui';
import { api } from '../lib/api';

const STARTERS = [
  'Explain my latest bloodwork in plain English.',
  'How much protein should I be eating for my goal?',
  'Is my current rate of weight loss sustainable?',
  'What should I prioritise in the gym this month?',
];

export default function Chat({ user }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const scroller = useRef(null);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const send = async (text) => {
    const question = (text ?? draft).trim();
    if (!question || busy) return;

    setMessages((m) => [...m, { role: 'user', text: question }]);
    setDraft('');
    setError('');
    setBusy(true);

    try {
      const res = await api.chat.ask(question, true);
      setMessages((m) => [...m, { role: 'ai', text: res.response, model: res.model, rag: res.rag_enabled }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div ref={scroller} className="chat-scroll">
        {messages.length === 0 && (
          <div style={{ margin: 'auto', maxWidth: 560, textAlign: 'center' }}>
            <div style={{
              width: 46, height: 46, borderRadius: 14, margin: '0 auto 14px',
              background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
              display: 'grid', placeItems: 'center', color: '#fff',
            }}>
              <Sparkles size={22} />
            </div>
            <h2 style={{ fontSize: 19 }}>Ask about your health</h2>
            <p className="dim" style={{ fontSize: 13.5, marginTop: 8 }}>
              Answers are grounded in 4,882 peer-reviewed abstracts and use your own profile,
              bloodwork and logs as context.
            </p>
            <div className="stack-tight" style={{ marginTop: 22 }}>
              {STARTERS.map((s) => (
                <button key={s} className="btn btn-ghost" style={{ justifyContent: 'flex-start', fontWeight: 500 }}
                  onClick={() => send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div className={`msg ${m.role === 'user' ? 'me' : 'ai'}`} key={i}>
            <span className="msg-avatar">
              {m.role === 'user' ? <UserIcon size={14} /> : <Sparkles size={14} />}
            </span>
            <div className="msg-body">
              {m.role === 'user'
                ? m.text
                : (
                  <>
                    <div className="md">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
                    </div>
                    {m.rag && (
                      <div style={{ marginTop: 10 }}>
                        <Badge tone="muted">Grounded in retrieved research</Badge>
                      </div>
                    )}
                  </>
                )}
            </div>
          </div>
        ))}

        {busy && (
          <div className="msg ai">
            <span className="msg-avatar"><Sparkles size={14} /></span>
            <div className="msg-body">
              <span className="typing"><i /><i /><i /></span>
            </div>
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--border)', padding: 'var(--gap-3) var(--gap-4)', background: 'var(--bg-elevated)' }}>
        {error && <div style={{ marginBottom: 10 }}><Alert tone="crit">{error}</Alert></div>}
        <div className="row" style={{ gap: 10, alignItems: 'flex-end' }}>
          <Textarea
            rows={1}
            placeholder="Ask anything about your health, training or nutrition…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            style={{ flex: 1, minHeight: 42, maxHeight: 160 }}
            disabled={busy}
          />
          <Button variant="primary" icon={Send} onClick={() => send()} disabled={busy || !draft.trim()}>
            Send
          </Button>
        </div>
        <Disclaimer>
          {user?.profile?.age
            ? 'Your profile, latest bloodwork and recent logs are sent with each question.'
            : 'Fill in your profile so answers can account for your age, sex and goals.'}
          {' '}This is general information, not medical advice — talk to a healthcare professional
          before acting on anything here.
        </Disclaimer>
      </div>
    </div>
  );
}
